import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  approveStaffRegistration,
  cancelLinkRequest,
  deactivateStaff,
  fetchPendingLinkRequests,
  fetchPendingStaffRegistrations,
  fetchStaff,
  fetchStaffSuggestions,
  reactivateStaff,
  rejectStaffRegistration,
} from '../../api/staff';
import AddStaffModal from '../../components/staff/AddStaffModal';
import StaffDetailDrawer from '../../components/staff/StaffDetailDrawer';
import DashboardShell from '../../components/ui/DashboardShell';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import EmptyState from '../../components/ui/EmptyState';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { ListSkeleton } from '../../components/ui/Skeleton';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';

const SalonStaffPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [activeFilter, setActiveFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['staff-suggestions', activeFilter],
    fetchSuggestions: (q) =>
      fetchStaffSuggestions({
        q,
        isActive: activeFilter || undefined,
      }),
  });

  const permChips = useMemo(
    () => [
      { key: 'canManageAppointments', short: t('salon.staff.permAppointments') },
      { key: 'canManageOrders', short: t('salon.staff.permOrders') },
      { key: 'canViewAnalytics', short: t('salon.staff.permAnalytics') },
      { key: 'canManageProducts', short: t('salon.staff.permProducts') },
      { key: 'canManageInventory', short: t('salon.staff.permInventory') },
      { key: 'canManageStaff', short: t('salon.staff.permStaff') },
      { key: 'canManageSalaries', short: t('salon.staff.permSalaries') },
      { key: 'canUsePos', short: t('salon.staff.permPos') },
    ],
    [t]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['staff', page, pageSize, activeFilter, appliedSearch],
    queryFn: () =>
      fetchStaff({
        page,
        limit: pageSize,
        isActive: activeFilter || undefined,
        search: appliedSearch || undefined,
      }),
  });

  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['staff-pending-registrations'],
    queryFn: fetchPendingStaffRegistrations,
  });

  const { data: pendingLinkData, isLoading: pendingLinkLoading } = useQuery({
    queryKey: ['staff-pending-link-requests'],
    queryFn: fetchPendingLinkRequests,
  });

  const pendingRegistrations = pendingData?.registrations || [];
  const pendingLinkRequests = pendingLinkData?.invitations || [];

  const staffList = data?.staff || [];
  const pagination = data?.pagination;

  const handleStaffAdded = (res) => {
    if (res.tempPassword) setTempPassword(res.tempPassword);
    showToast(res.message || t('toast.staffInvited'), 'success');
    queryClient.invalidateQueries({ queryKey: ['staff'] });
    if (res.pending) {
      queryClient.invalidateQueries({ queryKey: ['staff-pending-link-requests'] });
    }
  };

  const deactivateMut = useMutation({
    mutationFn: deactivateStaff,
    onSuccess: () => {
      showToast(t('toast.staffDeactivated'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });

  const reactivateMut = useMutation({
    mutationFn: reactivateStaff,
    onSuccess: () => {
      showToast(t('toast.staffReactivated'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });

  const approvePendingMut = useMutation({
    mutationFn: approveStaffRegistration,
    onSuccess: (res) => {
      showToast(res.message || t('auth.staffRegister.approved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['staff-pending-registrations'] });
    },
    onError: (err) => showToast(getFriendlyError(err), 'error'),
  });

  const rejectPendingMut = useMutation({
    mutationFn: rejectStaffRegistration,
    onSuccess: (res) => {
      showToast(res.message || t('auth.staffRegister.rejected'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff-pending-registrations'] });
    },
  });

  const cancelLinkMut = useMutation({
    mutationFn: cancelLinkRequest,
    onSuccess: (res) => {
      showToast(res.message || t('staff.linkRequests.cancelled'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff-pending-link-requests'] });
    },
    onError: (err) => showToast(getFriendlyError(err), 'error'),
  });

  return (
    <DashboardShell title={t('salon.staff.title')} subtitle={t('salon.staff.subtitle')}>
      {tempPassword && (
        <div className="mb-6 rounded-xl border border-emerald-800/50 bg-emerald-950/30 p-4 text-sm">
          <p className="font-medium text-emerald-300">{t('salon.staff.invitationSentTitle')}</p>
          <p className="mt-1 text-emerald-200/80">
            {t('salon.staff.tempPasswordHint', { password: tempPassword })}
          </p>
          <button type="button" onClick={() => setTempPassword(null)} className="mt-2 text-xs text-emerald-400 underline">
            {t('salon.staff.dismiss')}
          </button>
        </div>
      )}

      {(pendingLinkLoading || pendingLinkRequests.length > 0) && (
        <section className="mb-8 rounded-xl border border-sky-200 bg-sky-50/80 p-5 dark:border-sky-900/50 dark:bg-sky-950/20">
          <h2 className="font-semibold text-sky-900 dark:text-sky-200">{t('salon.staff.pendingLinkRequestsTitle')}</h2>
          <p className="mt-1 text-sm text-sky-800/80 dark:text-sky-200/70">{t('salon.staff.pendingLinkRequestsDesc')}</p>
          {pendingLinkLoading && <p className="mt-3 text-sm text-zinc-500">{t('common.loading')}</p>}
          <div className="mt-4 space-y-3">
            {pendingLinkRequests.map((row) => (
              <article
                key={row.permissionId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sky-200/80 bg-white p-4 dark:border-sky-900/40 dark:bg-zinc-900"
              >
                <div>
                  <p className="font-medium">{row.firstName} {row.lastName}</p>
                  <p className="text-sm text-zinc-500">{row.email || row.phone}</p>
                  <p className="mt-1 text-xs text-zinc-500">{new Date(row.requestedAt).toLocaleString()}</p>
                </div>
                <button
                  type="button"
                  disabled={cancelLinkMut.isPending}
                  onClick={() => cancelLinkMut.mutate(row.permissionId)}
                  className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700"
                >
                  {t('staff.linkRequests.cancel')}
                </button>
              </article>
            ))}
          </div>
        </section>
      )}

      {(pendingLoading || pendingRegistrations.length > 0) && (
        <section className="mb-8 rounded-xl border border-amber-200 bg-amber-50/80 p-5 dark:border-amber-900/50 dark:bg-amber-950/20">
          <h2 className="font-semibold text-amber-900 dark:text-amber-200">{t('salon.staff.pendingRegistrationsTitle')}</h2>
          <p className="mt-1 text-sm text-amber-800/80 dark:text-amber-200/70">{t('salon.staff.pendingRegistrationsDesc')}</p>
          {pendingLoading && <p className="mt-3 text-sm text-zinc-500">{t('common.loading')}</p>}
          {!pendingLoading && pendingRegistrations.length === 0 && (
            <p className="mt-3 text-sm text-zinc-500">{t('salon.staff.pendingRegistrationsEmpty')}</p>
          )}
          <div className="mt-4 space-y-3">
            {pendingRegistrations.map((row) => (
              <article
                key={row.permissionId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200/80 bg-white p-4 dark:border-amber-900/40 dark:bg-zinc-900"
              >
                <div>
                  <p className="font-medium">{row.firstName} {row.lastName}</p>
                  <p className="text-sm text-zinc-500">{row.email || row.phone}</p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {new Date(row.requestedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={rejectPendingMut.isPending}
                    onClick={() => rejectPendingMut.mutate(row.permissionId)}
                    className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700"
                  >
                    {t('common.reject')}
                  </button>
                  <button
                    type="button"
                    disabled={approvePendingMut.isPending}
                    onClick={() => approvePendingMut.mutate(row.permissionId)}
                    className="rounded-lg bg-glow-600 px-3 py-2 text-sm text-white hover:bg-glow-500"
                  >
                    {t('common.approve')}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <ListSearchToolbar
          className="mb-0 flex-1"
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearch={(v) => { commitSearch(v); setPage(1); }}
          suggestions={suggestions}
          suggestionsLoading={suggestionsLoading}
          searchPlaceholder={t('salon.staff.searchPlaceholder')}
          filtersOpen={filtersOpen}
          onToggleFilters={() => setFiltersOpen((o) => !o)}
        >
          <select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
          >
            <option value="">{t('salon.staff.allStaff')}</option>
            <option value="true">{t('common.active')}</option>
            <option value="false">{t('common.inactive')}</option>
          </select>
        </ListSearchToolbar>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/salon/staff-performance"
            className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-medium dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            {t('staffPerformance.title')}
          </Link>
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white"
          >
            {t('salon.staff.inviteButton')}
          </button>
        </div>
      </div>

      {isLoading && <ListSkeleton rows={3} />}

      {!isLoading && staffList.length === 0 && (
        <EmptyState
          icon="👥"
          title={t('salon.staff.inviteFirstTitle')}
          subtitle={t('salon.staff.inviteFirstDesc')}
          action={
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white"
            >
              {t('salon.staff.inviteStaff')}
            </button>
          }
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {staffList.map((member) => (
          <div key={member._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex items-start gap-3">
              {member.profilePicture ? (
                <img src={member.profilePicture} alt="" loading="lazy" className="h-12 w-12 rounded-full object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 font-medium">
                  {member.firstName?.[0]}{member.lastName?.[0]}
                </div>
              )}
              <div className="flex-1">
                <p className="font-medium">{member.firstName} {member.lastName}</p>
                <p className="text-xs text-zinc-600 dark:text-zinc-500">{member.phone || member.email}</p>
                <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] ${member.isActive ? 'bg-emerald-900/40 text-emerald-300' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-500'}`}>
                  {member.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1">
              {permChips.filter((c) => member.permissions?.[c.key]).map((c) => (
                <span key={c.key} className="rounded-full bg-glow-600/15 px-2 py-0.5 text-[10px] text-glow-300">
                  {c.short}
                </span>
              ))}
            </div>

            {member.services?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {member.services.map((svc) => (
                  <span key={svc._id} className="rounded-full bg-purple-900/30 px-2 py-0.5 text-[10px] text-purple-300">
                    {svc.name}
                  </span>
                ))}
              </div>
            )}

            {(!member.serviceIds || member.serviceIds.length === 0) && (
              <p className="mt-2 text-[10px] text-zinc-500">{t('salon.staff.noSpecialties')}</p>
            )}

            {member.lastLoginAt && (
              <p className="mt-2 text-[10px] text-zinc-600 dark:text-zinc-500">
                {t('salon.staff.lastLogin', { date: new Date(member.lastLoginAt).toLocaleDateString() })}
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => setSelected(member)} className="text-xs text-glow-400 hover:underline">
                {t('salon.staff.viewActivity')}
              </button>
              {member.isActive ? (
                <button
                  type="button"
                  onClick={() => setConfirmDeactivate(member)}
                  className="text-xs text-red-400 hover:underline"
                >
                  {t('salon.staff.deactivate')}
                </button>
              ) : (
                <button type="button" onClick={() => reactivateMut.mutate(member._id)} className="text-xs text-emerald-400 hover:underline">
                  {t('salon.staff.reactivate')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <AddStaffModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={handleStaffAdded}
      />

      <StaffDetailDrawer staff={selected} onClose={() => setSelected(null)} />

      <ConfirmDialog
        open={!!confirmDeactivate}
        title={t('salon.staff.deactivateTitle')}
        message={t('salon.staff.deactivateMessage', {
          name: `${confirmDeactivate?.firstName} ${confirmDeactivate?.lastName}`,
        })}
        confirmLabel={t('salon.staff.deactivate')}
        confirmVariant="danger"
        loading={deactivateMut.isPending}
        onCancel={() => setConfirmDeactivate(null)}
        onConfirm={() => {
          deactivateMut.mutate(confirmDeactivate._id, {
            onSettled: () => setConfirmDeactivate(null),
          });
        }}
      />
    </DashboardShell>
  );
};

export default SalonStaffPage;
